import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import { useLocation, useNavigate } from 'react-router-dom';
import { getIndexerErrorInfo, invalidUrlsFromError } from '../api/errors';
import { indexerAlertVariant } from '../lib/indexer-alert-variant';
import {
  useCancelProjectJobMutation,
  useInspectUrlsMutation,
  useLazyGetJobRetryUrlsQuery,
} from '../api/indexerApi';
import { InspectSchedulePanel } from '../components/InspectSchedulePanel';
import { JobResultsPanel } from '../components/JobResultsPanel';
import { NeedProject } from '../components/NeedProject';
import { IndexerChartCard } from '../components/IndexerChartCard';
import { WorkspacePagePreload } from '../components/preloads/IndexerPreloads';
import { RequestHistory } from '../components/RequestHistory';
import { SitemapUrlImport } from '../components/SitemapUrlImport';
import { StatsCards, INSPECT_DAILY_QUOTA } from '../components/StatsCards';
import { InspectPageCharts } from '../components/charts/InspectPageCharts';
import { InspectQuotaHelpTip } from '../components/InspectQuotaHelpTip';
import { UrlListField } from '../components/UrlListField';
import {
  WorkspaceModeSwitch,
  type WorkspaceMode,
} from '../components/WorkspaceModeSwitch';
import { useProject } from '../lib/project-context';
import { useJobHistory } from '../lib/use-job-history';
import type { ProjectJob } from '../api/types';
import {
  inspectUsedToday,
  jobsForPageStats,
  type StatsRange,
} from '../lib/job-range';
import { formatFaNumber, mergeUrlLists, parseUrlList, validateProjectUrlList } from '../lib/utils';
import {
  type InspectPageLocationState,
  type InspectScheduleCreatePrefill,
} from '../lib/inspect-schedule-prefill';
import {
  readRouteUrlPrefill,
  stashInspectUrlPrefill,
  takeInspectUrlPrefill,
} from '../lib/url-list-prefill';

import { INSPECT_MAX_URLS_PER_JOB } from '../lib/inspect-limits';

type PageTab = WorkspaceMode;

function inspectPrefillMessage(source?: string): string {
  if (source === 'inspect-bulk-reinspect') {
    return 'آدرس‌های این درخواست دوباره در فرم وضعیت ایندکس قرار گرفت. برای اجرای مجدد، بررسی وضعیت را ارسال کنید.';
  }
  if (source === 'index-history') {
    return 'آدرس‌ها از درخواست ایندکس به فرم وضعیت ایندکس منتقل شد. در صورت نیاز بررسی را ارسال کنید.';
  }
  return 'آدرس در فرم وضعیت ایندکس قرار گرفت. در صورت نیاز درخواست را ارسال کنید.';
}

export function InspectPage() {
  const { project, projectRef } = useProject();
  const location = useLocation();
  const navigate = useNavigate();
  const [inspectUrls] = useInspectUrlsMutation();
  const [cancelProjectJob] = useCancelProjectJobMutation();
  const [getJobRetryUrls] = useLazyGetJobRetryUrlsQuery();
  const [pageTab, setPageTab] = useState<PageTab>('charts');
  const [urlText, setUrlText] = useState('');
  const [sitemapLastmods, setSitemapLastmods] = useState<Record<string, string>>(
    {},
  );
  const [busy, setBusy] = useState(false);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [stoppingJobId, setStoppingJobId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(
    null,
  );
  const [rangeDays, setRangeDays] = useState<StatsRange>(7);
  const [formError, setFormError] = useState<string | null>(null);
  const [schedulePrefill, setSchedulePrefill] =
    useState<InspectScheduleCreatePrefill | null>(null);
  const formActionsRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const urls = useMemo(() => parseUrlList(urlText), [urlText]);
  const inspectValidationError = useMemo(
    () =>
      project ? validateProjectUrlList(urls, project, INSPECT_MAX_URLS_PER_JOB) : null,
    [urls, project],
  );
  const history = useJobHistory(projectRef ?? undefined, 'inspect');
  const rangedJobs = useMemo(
    () => jobsForPageStats(history.jobs, rangeDays, pageTab),
    [history.jobs, rangeDays, pageTab],
  );
  const todayInspectUsed = useMemo(
    () => inspectUsedToday(history.jobs),
    [history.jobs],
  );
  const inspectQuotaRemaining = Math.max(
    0,
    INSPECT_DAILY_QUOTA - todayInspectUsed,
  );

  const prefillAppliedRef = useRef(false);

  useLayoutEffect(() => {
    const state = location.state as InspectPageLocationState | null;

    if (state?.schedulePrefill?.urls?.length) {
      setPageTab('schedule');
      setSchedulePrefill(state.schedulePrefill);
      setMessage({
        variant: 'info',
        text: 'فرم زمان‌بندی با این آدرس و رویداد «فقط وقتی وضعیت عوض شد» باز شد.',
      });
      navigate('.', { replace: true, state: null });
      return;
    }

    const fromStorage = takeInspectUrlPrefill();
    const fromRoute = fromStorage ? null : readRouteUrlPrefill(location.state);
    if (fromRoute) {
      stashInspectUrlPrefill(fromRoute.urls, fromRoute.source);
      navigate('.', { replace: true, state: null });
    }

    const payload = fromStorage ?? fromRoute;
    if (!payload || prefillAppliedRef.current) return;

    prefillAppliedRef.current = true;
    setUrlText(payload.urls.slice(0, INSPECT_MAX_URLS_PER_JOB).join('\n'));
    setPageTab('manual');
    setMessage({
      variant: 'info',
      text: inspectPrefillMessage(payload.source),
    });
  }, [location.state, location.key, navigate, project?.id]);

  useEffect(() => {
    prefillAppliedRef.current = false;
  }, [location.key]);

  useEffect(() => {
    setFormError(null);
  }, [urlText]);

  const showJobResults = Boolean(
    history.selectedId && pageTab === 'charts',
  );

  function onImportFromSitemap(
    selected: string[],
    options?: { lastmods?: Record<string, string> },
  ) {
    if (selected.length === 0) return;
    const merged = mergeUrlLists(urls, selected, INSPECT_MAX_URLS_PER_JOB);
    setUrlText(merged.join('\n'));
    if (options?.lastmods) {
      setSitemapLastmods((prev) => ({ ...prev, ...options.lastmods }));
    }
    setMessage({
      variant: 'success',
      text: `${selected.length} آدرس از نقشه سایت به لیست اضافه شد.`,
    });
  }

  function showFormValidationError(text: string) {
    setMessage({ variant: 'danger', text });
    setFormError(text);
    requestAnimationFrame(() => {
      formActionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  async function onStopJob(job: ProjectJob) {
    if (!project || !projectRef || stoppingJobId) return;
    setMessage(null);
    setStoppingJobId(job.id);
    try {
      const updated = await cancelProjectJob({
        projectId: projectRef,
        jobId: job.id,
      }).unwrap();
      setMessage({
        variant: 'info',
        text:
          updated.state === 'cancelled'
            ? 'درخواست متوقف شد. آدرس‌های باقی‌مانده اجرا نمی‌شوند.'
            : 'توقف ثبت شد. آدرس جاری تمام می‌شود و بقیه اجرا نمی‌شوند.',
      });
      await history.refresh();
      await history.refreshSelected();
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'توقف درخواست ناموفق بود',
      });
    } finally {
      setStoppingJobId(null);
    }
  }

  function showPageMessage(next: { variant: string; text: string }) {
    setMessage(next);
    requestAnimationFrame(() => {
      messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  async function onRetryJob(job: ProjectJob) {
    if (!project || !projectRef || retryingJobId) return;
    setMessage(null);
    setFormError(null);
    setRetryingJobId(job.id);
    try {
      const plan = await getJobRetryUrls(
        { projectId: projectRef, jobId: job.id },
        true,
      ).unwrap();
      const targetUrls = plan.urls;
      if (targetUrls.length === 0) {
        showPageMessage({
          variant: 'info',
          text:
            plan.skippedSucceededElsewhere > 0
              ? `آدرس باقی‌مانده‌ای نیست؛ ${formatFaNumber(plan.skippedSucceededElsewhere)} آدرس در تلاش‌های بعدی همین درخواست با موفقیت انجام شده‌اند.`
              : 'آدرس باقی‌مانده یا ناموفقی برای تلاش مجدد در این درخواست نیست.',
        });
        return;
      }
      if (targetUrls.length > inspectQuotaRemaining) {
        showPageMessage({
          variant: 'danger',
          text:
            inspectQuotaRemaining === 0
              ? `سهمیه روزانه بازرسی تمام شده است (حداکثر ${formatFaNumber(INSPECT_DAILY_QUOTA)} آدرس در ۲۴ ساعت گذشته).`
              : `فقط ${formatFaNumber(inspectQuotaRemaining)} آدرس از سهمیه ۲۴ ساعته مانده است؛ ${formatFaNumber(targetUrls.length)} آدرس برای تلاش مجدد دارید.`,
        });
        return;
      }
      const queued = await inspectUrls({
        projectId: projectRef,
        urls: targetUrls,
        retryOf: job.id,
      }).unwrap();
      const skippedNote =
        plan.skippedSucceededElsewhere > 0
          ? ` (${formatFaNumber(plan.skippedSucceededElsewhere)} آدرس موفق قبلی رد شد)`
          : '';
      showPageMessage({
        variant: 'info',
        text: `تلاش مجدد برای ${formatFaNumber(targetUrls.length)} آدرس در صف قرار گرفت${skippedNote}.`,
      });
      setPageTab('charts');
      await history.openJob(queued.jobId);
    } catch (err) {
      showPageMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'تلاش مجدد ناموفق بود',
      });
    } finally {
      setRetryingJobId(null);
    }
  }

  async function onInspect() {
    if (!project || !projectRef) return;
    setMessage(null);
    if (inspectValidationError) {
      showFormValidationError(inspectValidationError);
      return;
    }
    if (urls.length > inspectQuotaRemaining) {
      showFormValidationError(
        inspectQuotaRemaining === 0
          ? `سهمیه روزانه بازرسی تمام شده است (حداکثر ${formatFaNumber(INSPECT_DAILY_QUOTA)} آدرس در ۲۴ ساعت گذشته).`
          : `فقط ${formatFaNumber(inspectQuotaRemaining)} آدرس از سهمیه ۲۴ ساعته مانده است؛ لیست فعلی ${formatFaNumber(urls.length)} آدرس دارد.`,
      );
      return;
    }
    setBusy(true);
    try {
      const lastmods: Record<string, string> = {};
      for (const url of urls) {
        if (sitemapLastmods[url]) lastmods[url] = sitemapLastmods[url];
      }
      const queued = await inspectUrls({
        projectId: projectRef,
        urls,
        lastmods: Object.keys(lastmods).length > 0 ? lastmods : undefined,
      }).unwrap();
      setMessage({
        variant: 'info',
        text: `جاب درخواست در صف قرار گرفت (${queued.total} آدرس). نتایج به‌تدریج در تاریخچه می‌آیند.`,
      });
      setPageTab('charts');
      await history.openJob(queued.jobId);
    } catch (err) {
      const info = getIndexerErrorInfo(err);
      const invalid = invalidUrlsFromError(err);
      const httpPrefix = info.code ? `HTTP ${info.code} — ` : '';
      setMessage({
        variant: 'danger',
        text:
          invalid.length > 0
            ? `${httpPrefix}${info.message} — نامعتبر: ${invalid.join(', ')}`
            : `${httpPrefix}${info.message}`,
      });
    } finally {
      setBusy(false);
    }
  }

  if (!project || !projectRef) {
    return <NeedProject />;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {message && (
        <div ref={messageRef}>
          <IconAlert
            variant={indexerAlertVariant(message.variant)}
            dismissible
            onClose={() => setMessage(null)}
          >
            <p className="mb-0 text-justify pe-3">{message.text}</p>
          </IconAlert>
        </div>
      )}

      {history.loadingList && history.jobs.length === 0 ? (
        <WorkspacePagePreload />
      ) : (
        <>
      <StatsCards
        jobs={rangedJobs}
        rangeDays={rangeDays}
        onRangeChange={setRangeDays}
        dailyQuota={{
          used: todayInspectUsed,
          limit: INSPECT_DAILY_QUOTA,
          label: 'وضعیت ایندکس',
          help: <InspectQuotaHelpTip />,
        }}
      />

      <WorkspaceModeSwitch
        value={pageTab}
        onChange={setPageTab}
        context="inspect"
      />

      <div>
        {pageTab === 'manual' ? (
          <IndexerChartCard
            className="mb-0"
            title="ارسال دستی"
            subtitle="URL یا sitemap را برای بازرسی وضعیت ایندکس بفرستید"
          >
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <Form.Label className="mb-0">
                  لیست آدرس‌ها (حداکثر {INSPECT_MAX_URLS_PER_JOB})
                </Form.Label>
                <SitemapUrlImport
                  projectId={projectRef}
                  project={project}
                  limit={INSPECT_MAX_URLS_PER_JOB}
                  existingUrls={urls}
                  onImport={onImportFromSitemap}
                />
              </div>
              <UrlListField
                project={project}
                value={urlText}
                limit={INSPECT_MAX_URLS_PER_JOB}
                onChange={setUrlText}
              />

              {formError && (
                <Alert
                  variant="danger"
                  className="mt-3 mb-0 py-2"
                  dismissible
                  onClose={() => setFormError(null)}
                >
                  {formError}
                </Alert>
              )}

              <div ref={formActionsRef} className="pt-2">
                <div className="d-flex flex-wrap justify-content-end align-items-center gap-2">
                  <Button
                    variant="primary"
                    disabled={busy || inspectQuotaRemaining === 0}
                    onClick={() => void onInspect()}
                  >
                    {busy && (
                      <Spinner
                        size="sm"
                        animation="border"
                        className="me-2"
                        style={{ width: 12, height: 12 }}
                      />
                    )}
                    بررسی وضعیت
                  </Button>
                </div>
              </div>
          </IndexerChartCard>
        ) : pageTab === 'schedule' ? (
          <IndexerChartCard
            className="mb-0"
            title="زمان‌بندی"
            subtitle="تکرار خودکار بازرسی وضعیت ایندکس و اطلاع‌رسانی"
          >
            <InspectSchedulePanel
              projectId={projectRef}
              createPrefill={schedulePrefill}
              onCreatePrefillConsumed={() => setSchedulePrefill(null)}
            />
          </IndexerChartCard>
        ) : (
          <InspectPageCharts
            projectId={projectRef}
            rangeDays={rangeDays}
            jobs={rangedJobs}
          />
        )}
      </div>

      {pageTab === 'charts' && (
        <div>
          <RequestHistory
            title="تاریخچه"
            jobs={history.jobs}
            trigger="all"
            selectedId={history.selectedId}
            onSelect={history.select}
            loading={history.loadingList}
            onRetry={(job) => void onRetryJob(job)}
            retryingJobId={retryingJobId}
            onStop={(job) => void onStopJob(job)}
            stoppingJobId={stoppingJobId}
            inspectBulkActions={{
              projectId: projectRef,
              onNotify: (next) => setMessage(next),
            }}
          />
        </div>
      )}
        </>
      )}

      <JobResultsPanel
        show={showJobResults}
        projectId={projectRef}
        job={history.selectedJob}
        loading={history.loadingJob}
        onClose={history.close}
        onNotify={(next) => setMessage(next)}
        onRetry={(job) => void onRetryJob(job)}
        retryBusy={!!retryingJobId}
        onStop={(job) => void onStopJob(job)}
        stopBusy={!!stoppingJobId}
        onLiveProgress={history.applyLiveProgress}
        onLiveRefresh={() => {
          void history.refreshSelected();
          void history.refresh();
        }}
      />
    </div>
  );
}
